<script setup lang="ts">

import {useRoute, useRouter} from "vue-router";
import useSwr from "@/core/composables/swr/useSwr";
import {computed, ref, watch} from "vue";
import {cacheKey} from "@/lib/cache";
import {flowchartApi} from "@/container";
import {Edge, MarkerType, VueFlow} from "@vue-flow/core";
import CustomNode from "@/flowcharts/Components/CustomNode.vue";
import {Flowchart} from "@/core/helpers/flowchart";
import {Label} from "@/core/components/ui/label";
import {Switch} from "@/core/components/ui/switch";
import {FlowchartEdge, FlowchartNode, ResolvedAction, SurveyResolvedAction, SurveyNodeVerdict} from "@/core/interfaces/flowchart/flowchart";
import ActionModal from "@/flowcharts/Components/ActionModal.vue";
import InvoicingDialog from "@/flowcharts/Components/InvoicingDialog.vue";
import {usePatientStore} from "@/stores/patient.store";
import {useSystemStore} from "@/stores/system.store";
import useFlowchartNodePositions from "@/flowcharts/composables/useFlowchartNodePositions";
import useFlowchartInstance from "@/flowcharts/composables/useFlowchartInstance";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {storeToRefs} from "pinia";
import FlowchartConsumerSwitch from "@/flowcharts/Components/FlowchartConsumerSwitch.vue";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {provideMSSanteSource} from "@/mssante/composables/useMSSanteSource";

const route = useRoute();
const router = useRouter();
const flowchart = ref<Flowchart>();
const editMode = defineModel<boolean>({default: false});
const activatedNode = ref<FlowchartNode>();
const {customer} = storeToRefs(usePatientStore());
const {isAdmin} = storeToRefs(useSystemStore());
const renderKey = ref<string>('');
const instance = useFlowchartInstance();

// Run from a support program: the MSSanté messages of the TROD are linked to it.
provideMSSanteSource(() => route.query.support_program_id
    ? {type: 'support', id: Number(route.query.support_program_id)}
    : undefined);

/**
 * The patient the flowchart is run for: a beneficiary picked in the route query,
 * otherwise the insured customer.
 */
const consumer = computed((): Customer | Beneficiary | undefined => {
    const key = route.query.consumer_key;

    return customer.value?.beneficiaries.find(beneficiary => beneficiary.consumer_key === key)
        ?? customer.value ?? undefined;
});

/** The consumer key waiting for the pharmacist to confirm the reset of the current passage. */
const pendingConsumerKey = ref<string>();

/**
 * Whether the reset confirmation is open. Kept apart from the pending key: the dialog
 * closes itself before the confirm click handler runs.
 */
const confirmingConsumer = ref<boolean>(false);

/** Resolved survey verdicts, keyed by survey node id. */
const surveyVerdicts = ref<Record<number, SurveyNodeVerdict>>({});

window.addEventListener('resize', () => {
    renderKey.value = window.innerHeight + '-' + window.innerWidth;
});

/**
 * Fetches fraud reports from the ASAFO API.
 * Uses SWR for data fetching and caching.
 */
const {data, isLoading} = useSwr(
    computed(() => cacheKey(`flowcharts/${route.params.id}`)),
    () => flowchartApi().show(String(route.params.id)),
);

const {
    hasMovedNodes,
    savingPositions,
    handleNodeDragStop,
    resetMovedPositions,
    saveNodePositions
} = useFlowchartNodePositions(data);

watch(data, () => reset(true));

/**
 * Map node data to node props.
 */
const nodes = computed(() => data?.value?.nodes.map((node, index) => ({
    id: node.id.toString(),
    type: 'custom',
    position: JSON.parse(node.position) as { x: number, y: number },
    data: {
        ...node,
        highlighted: index === 0 || flowchart.value?.isNodeHighlighted(node.id),
        secondaryHighlighted: false,
        surveyVerdict: surveyVerdicts.value[node.id],
    }
})) || []);

/**
 * Map edge data to edge props.
 */
const edges = computed(() => data?.value?.edges.map(edge => ({
    id: Flowchart.edgeId(edge),
    source: edge.from_node_id.toString(),
    target: edge.to_node_id.toString(),
    sourceHandle: 'h' + edge.from_handle_id,
    targetHandle: 'h' + edge.to_handle_id,
    type: 'smoothstep',
    label: edge.label,
    markerEnd: {type: MarkerType.ArrowClosed},
    style: edgeStyle(edge),
    animated: flowchart.value?.isEdgeSecondaryHighlighted(edge),
    labelBgBorderRadius: 10,
    labelBgPadding: [8, 3],
    labelBgStyle: {fill: flowchart.value?.isEdgeHighlighted(edge) ? '#7fde85' : '#d7dce8'},
    labelStyle: {fontSize: '11px'}
}))
);

/**
 * Compute the edge style
 * @param edge The edge to style.
 */
const edgeStyle = (edge: FlowchartEdge) => ({
    stroke: flowchart.value?.isEdgeHighlighted(edge) ? '#36d33f' : '#d7dce8',
    strokeWidth: flowchart.value?.isEdgeSecondaryHighlighted(edge) ? 2 : 1
});

/**
 * Handles the click event on a node.
 * @param node
 */
const handleNodeClick = ({node}) => {
    if (!editMode.value) {
        flowchart.value!.highlightToNode(node);
    }
};

/**
 * Handles the click event on an edge.
 * @param edge The clicked edge.
 */
const handleEdgeClick = ({edge}) => {
    if (!editMode.value) {
        flowchart.value!.highlightToEdge(edge);
    }
};

/**
 * Handle a resolved survey verdict: display it on the survey node and automatically
 * select the next node by following the outgoing edge whose slug matches the verdict slug.
 *
 * @param action The resolved survey action.
 */
const handleSurveyResolved = ({node, verdict}: SurveyResolvedAction) => {
    if (!verdict) {
        return;
    }

    surveyVerdicts.value[node.id] = verdict;

    const targetEdge = data.value?.edges.find(
        edge => edge.from_node_id === node.id && edge.slug === verdict.slug
    );

    if (targetEdge) {
        flowchart.value!.highlightToEdge({
            id: Flowchart.edgeId(targetEdge),
            target: targetEdge.to_node_id.toString()
        } as Edge);
    }
};

/**
 * Handle any resolved action: dispatch to the type-specific handler, then close the modal.
 *
 * @param action The discriminated resolved action payload.
 */
const onActionResolved = (action: ResolvedAction) => {
    if (action.type === 'survey') {
        handleSurveyResolved(action);
    }

    activatedNode.value = undefined;
};

/**
 * Resets the paths, selected path and survey verdicts to the initial state.
 *
 * @param force When true, reset even in edit mode (used when the flowchart data changes).
 */
const reset = (force = false) => {
    if (force || !editMode.value) {
        flowchart.value = new Flowchart(data.value!);
        surveyVerdicts.value = {};
        instance.reset();
    }

    if (force) {
        resetMovedPositions();
    }
};

/**
 * Run the flowchart for another patient. The current passage belongs to the previous
 * patient, so it is reset, after a confirmation once the pharmacist has started it.
 *
 * @param key The consumer key of the patient.
 */
const selectConsumer = (key: string) => {
    if (key === consumer.value?.consumer_key) {
        return;
    }

    const started = !!flowchart.value?.selectedEdges().length || !!Object.keys(surveyVerdicts.value).length;

    if (started) {
        pendingConsumerKey.value = key;
        confirmingConsumer.value = true;
    } else {
        applyConsumer(key);
    }
};

/**
 * Store the selected patient in the route query, the insured customer being the default.
 *
 * @param key The consumer key of the patient.
 */
const applyConsumer = (key: string | undefined) => {
    router.replace({
        query: {
            ...route.query,
            consumer_key: key && key !== customer.value?.consumer_key ? key : undefined,
        }
    });
};

// A new passage starts for the selected patient, once both the patient and the flowchart are loaded.
watch(() => consumer.value?.consumer_key, (key, previous) => {
    if (previous && data.value) {
        reset();
    }
});

// A key that is not one of the current patient's family falls back to the insured customer.
watch([() => route.query.consumer_key, () => customer.value?.id], ([key]) => {
    if (key && customer.value && consumer.value?.consumer_key !== key) {
        applyConsumer(undefined);
    }
}, {immediate: true});

/**
 * Download a file.
 */
const downloadFile = () => {
    if (data.value?.url) {
        window.open(data.value.url);
    }
};
</script>

<template>
  <BaseFlexSpinner v-if="isLoading && !data" class="self-center" />
  <div v-else class="relative flex flex-col h-full border justify-center">
    <div class="z-100 absolute top-2 right-2 flex flex-col items-end space-y-2">
      <div class="flex items-center space-x-2">
        <InvoicingDialog v-if="data?.survey?.invoicing" :invoicing="data.survey.invoicing" />
        <template v-if="isAdmin">
          <Label for="edit" class="text-xs">Modifier</Label>
          <Switch id="edit" :model-value="editMode" @update:model-value="editMode = !editMode" />
        </template>
      </div>
      <BaseButton
        v-if="isAdmin && editMode"
        density="comfortable" prepend-icon="mdi-content-save-outline"
        :disabled="!hasMovedNodes || savingPositions"
        @click="saveNodePositions"
      >
        Enregistrer les positions
      </BaseButton>
      <BaseButton
        v-if="data?.url" density="comfortable" prepend-icon="mdi-file-download-outline"
        @click="downloadFile"
      >
        Télécharger le modèle
      </BaseButton>
      <BaseButton
        density="comfortable" prepend-icon="mdi-restore"
        @click="reset()"
      >
        Réinitialiser
      </BaseButton>
    </div>
    <div class="flex items-center gap-3 p-2 pb-5">
      <h1 class="text-2xl font-bold">
        {{ data?.name }}
      </h1>
      <FlowchartConsumerSwitch
        v-if="customer && consumer"
        :customer="customer"
        :model-value="consumer.consumer_key"
        @update:model-value="selectConsumer"
      />
    </div>
    <VueFlow
      :key="renderKey"
      :nodes="nodes"
      :edges="edges as Edge[]"
      :zoom-on-scroll="false"
      :fit-view-on-init="true"
      :zoom-on-double-click="false"
      :nodes-draggable="editMode"
      :pan-on-drag="editMode"
      :pan-on-scroll="true"
      @node-click="handleNodeClick"
      @edge-click="handleEdgeClick"
      @node-drag-stop="handleNodeDragStop"
    >
      <template #[`node-custom`]="props">
        <CustomNode
          :id="props.id"
          :position="props.position"
          :data="props.data"
          :edit-mode="editMode"
          @action-click="activatedNode = $event"
        />
      </template>
    </VueFlow>
    <ActionModal
      v-if="activatedNode && flowchart && consumer"
      :flowchart="flowchart"
      :node="activatedNode"
      :consumer="consumer"
      :survey-verdicts="surveyVerdicts"
      :instance="instance"
      @close="activatedNode = undefined"
      @resolved="onActionResolved"
    />
    <AlertDialog v-model:open="confirmingConsumer">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Changer de patient ?</AlertDialogTitle>
          <AlertDialogDescription>
            Le parcours en cours sera réinitialisé.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            Annuler
          </AlertDialogCancel>
          <AlertDialogAction class="!bg-primary" @click="applyConsumer(pendingConsumerKey)">
            Changer de patient
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>

<style>
/* import the necessary styles for Vue Flow to work */
@import '@vue-flow/core/dist/style.css';

/* import the default theme, this is optional but generally recommended */
@import '@vue-flow/core/dist/theme-default.css';
</style>
